import { useEffect, useState } from 'react';
import { RadioChangeEvent } from 'antd';
import { get } from 'lodash';
import {
  Button,
  ENotificationType,
  Form,
  FormItem,
  Input,
  Modal,
  TextArea,
  useForm,
  useShowToastNotification,
  useTranslation,
  filterLabelOption,
} from '@packages/utils';
import { TValidateFieldsError } from 'data/types/generic.types';
import { EIconNames, Icon } from 'components/Base/Icon';
import { Select } from 'components/Base/Select';
import { RadioGroup } from 'components/Base/RadioGroup';
import {
  useAddAdditionalEmailMutation,
  useAddAdditionalPhoneMutation,
} from 'redux/apiSlice/organizations.slice';
import {
  useAddPeopleOrganizationsMutation,
  useGetPeopleIdentityQuery,
} from 'redux/apiSlice/people.slice';
import {
  TAddInfoFormProps,
  TAddInfoProps,
  TInfoTypeProps,
} from './AddInfo.types';
import useGetFormData from './AddInfo.data';

const AddInfo = ({
  allowActionsEmail,
  allowActionsMobile,
  organizationId,
}: TAddInfoProps) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.organizations.details.details-tab.actions.add',
  });
  const { showToastNotification } = useShowToastNotification();
  const { form } = useForm<TAddInfoFormProps>();
  const [open, setOpen] = useState<boolean>(false);
  let defaultSelectedType: TInfoTypeProps;

  if (allowActionsEmail.canAdd) {
    defaultSelectedType = 'email';
  } else if (allowActionsMobile.canAdd) {
    defaultSelectedType = 'mobile';
  } else {
    defaultSelectedType = 'people';
  }

  const [selectedType, setSelectedType] =
    useState<TInfoTypeProps>(defaultSelectedType);
  const { addInfoFormFields } = useGetFormData();
  const { userId, email, mobile, title, infoType } = addInfoFormFields;
  const [addAdditionalEmail, { isLoading: addAdditionalEmailLoading }] =
    useAddAdditionalEmailMutation();
  const [addAdditionalPhone, { isLoading: addAdditionalPhoneLoading }] =
    useAddAdditionalPhoneMutation();
  const [addPeopleOrganizations, { isLoading: addPeopleOrganizationsLoading }] =
    useAddPeopleOrganizationsMutation();
  const { data: peopleIdentityOptions, isLoading } =
    useGetPeopleIdentityQuery();

  useEffect(() => {
    setSelectedType(defaultSelectedType);
  }, [defaultSelectedType]);

  const infoTypeOptions = [
    ...(allowActionsEmail.canAdd
      ? [
          {
            value: 'email',
            label: t('modal.form.email.LABEL'),
          },
        ]
      : []),
    ...(allowActionsMobile.canAdd
      ? [
          {
            value: 'mobile',
            label: t('modal.form.mobile.LABEL'),
          },
        ]
      : []),
    {
      value: 'people',
      label: t('modal.form.people.LABEL'),
    },
  ];

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const onChange = (e: RadioChangeEvent) => {
    setSelectedType(e.target.value);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (selectedType === 'email') {
        await addAdditionalEmail({ organizationId, ...values }).unwrap();
      }
      if (selectedType === 'mobile') {
        await addAdditionalPhone({ organizationId, ...values }).unwrap();
      }
      if (selectedType === 'people') {
        await addPeopleOrganizations({ organizationId, ...values }).unwrap();
      }
      showToastNotification({
        message: t('modal.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
      handleClose();
    } catch (err) {
      if (!(err as TValidateFieldsError)?.errorFields) {
        const message = get(err, 'data.message');
        showToastNotification({
          message,
          type: ENotificationType.ERROR,
        });
      }
    }
  };

  const renderFormItems = (type: TInfoTypeProps) => {
    const formItems: {
      [key in TInfoTypeProps]: React.ReactElement;
    } = {
      email: (
        <FormItem {...email}>
          <Input placeholder={email.placeholder} />
        </FormItem>
      ),
      mobile: (
        <FormItem {...mobile}>
          <Input placeholder={mobile.placeholder} />
        </FormItem>
      ),
      people: (
        <>
          <FormItem {...userId}>
            <Select
              filterOption={filterLabelOption}
              loading={isLoading}
              showSearch
              placeholder={userId.placeholder}
              options={peopleIdentityOptions}
            />
          </FormItem>
          <FormItem {...title}>
            <TextArea
              maxLength={250}
              showCount
              placeholder={title.placeholder}
            />
          </FormItem>
        </>
      ),
    };

    return formItems[type] || null;
  };

  return (
    <>
      <Button
        color="primary"
        type="primary"
        onClick={() => setOpen(true)}
        icon={<Icon icon={EIconNames.PLUS} className="pointer" />}
      >
        {t('ADD_BTN')}
      </Button>
      <Modal
        title={t('modal.TITLE')}
        open={open}
        onCancel={handleClose}
        onOk={handleSubmit}
        centeredPosition
        okText={t('modal.SUBMIT_BTN')}
        cancelText={t('modal.CANCEL_BTN')}
        confirmLoading={
          addAdditionalEmailLoading ||
          addAdditionalPhoneLoading ||
          addPeopleOrganizationsLoading
        }
        destroyOnClose
      >
        <Form form={form}>
          {(allowActionsMobile.canAdd || allowActionsEmail.canAdd) && (
            <FormItem {...infoType}>
              <RadioGroup
                onChange={onChange}
                optionType="button"
                buttonStyle="solid"
                fullWidth
                options={infoTypeOptions}
                defaultValue={selectedType}
              />
            </FormItem>
          )}
          {renderFormItems(selectedType)}
        </Form>
      </Modal>
    </>
  );
};

export default AddInfo;
